<script lang="ts">
  import { resolveTheme, toStyleVars, deriveLinkLayout } from '@/lib/profile-themes'
  import type { ProfileThemeName, ThemeConfig } from '@/lib/profile-themes'
  import ProfileAvatar from '@/components/profile/ProfileAvatar.svelte'
  import ProfileHeader from '@/components/profile/ProfileHeader.svelte'
  import ProfileSocialRow from '@/components/profile/ProfileSocialRow.svelte'
  import ProfileStatsRow from '@/components/profile/ProfileStatsRow.svelte'
  import ProfileLinkList from '@/components/profile/ProfileLinkList.svelte'
  import ProfileShareCard from '@/components/profile/ProfileShareCard.svelte'
  import ProfileCaptureCard from '@/components/profile/ProfileCaptureCard.svelte'
  import ProfileFooter from '@/components/profile/ProfileFooter.svelte'

  type SocialLink = { platform: string; url: string }
  type ViewLink = { title: string; description?: string | null; type: 'post' | 'external' | 'file'; url: string; emoji?: string | null; thumbnailUrl?: string | null; clicks?: number | null; date?: string | null }
  type ViewPresentation = { slug: string; title: string; description?: string; thumbnail_url?: string; category?: string; presented_at?: string; is_published?: boolean; show_on_profile?: boolean }
  type ViewProfile = {
    displayName: string
    username: string
    /** 🔴 서버가 합쳐 준 소속/부서/팀 한 문자열. 여기서 다시 조립하지 않는다. */
    department: string
    bio: string
    avatarInitial: string
    isVerified: boolean
    role: string
    stats: { posts: number; saves: number; presentations: number }
  }

  let {
    theme = 'clean',
    themeConfig = {} as ThemeConfig,
    profile = { displayName: 'Profile', username: '', department: '', bio: '', avatarInitial: '?', stats: { posts: 0, saves: 0, presentations: 0 }, isVerified: false, role: 'user' } as ViewProfile,
    links = [],
    presentations = [],
    total_presentations_count = 0,
    is_owner = false,
    socialLinks = [] as SocialLink[],
    shortUrl = null,
    copiedShortUrl = false,
    emailCaptureEnabled = false,
    emailCaptureTitle = '소식 받기',
    subscribeMessage = '',
    subscribeError = '',
    subscriberEmail = '',
    subscriberName = '',
    subscribing = false,
    onCopyShortUrl = undefined as undefined | (() => void | Promise<void>),
    onSubscriberEmailChange = undefined as undefined | ((value: string) => void),
    onSubscriberNameChange = undefined as undefined | ((value: string) => void),
    onSubscribe = undefined as undefined | (() => void | Promise<void>),
    compact = false,
    showTopActions = true,
    showStats = true,
  }: {
    theme?: string
    themeConfig?: ThemeConfig
    profile?: ViewProfile
    links?: ViewLink[]
    presentations?: ViewPresentation[]
    total_presentations_count?: number
    is_owner?: boolean
    socialLinks?: SocialLink[]
    shortUrl?: string | null
    copiedShortUrl?: boolean
    emailCaptureEnabled?: boolean
    emailCaptureTitle?: string
    subscribeMessage?: string
    subscribeError?: string
    subscriberEmail?: string
    subscriberName?: string
    subscribing?: boolean
    onCopyShortUrl?: (() => void | Promise<void>) | undefined
    onSubscriberEmailChange?: ((value: string) => void) | undefined
    onSubscriberNameChange?: ((value: string) => void) | undefined
    onSubscribe?: (() => void | Promise<void>) | undefined
    compact?: boolean
    showTopActions?: boolean
    /** 활동 통계를 비공개로 둔 프로필에서는 서버가 stats 를 아예 안 보낸다. 그때 0 줄을 그리지 않으려는 것. */
    showStats?: boolean
  } = $props()

  const resolvedTokens = $derived(resolveTheme(theme as ProfileThemeName, themeConfig))
  const styleVars = $derived(toStyleVars(resolvedTokens))
  const linkLayout = $derived(deriveLinkLayout(theme as ProfileThemeName))
  const normalizedLinks = $derived(
    links.map((link) => ({
      ...link,
      description: link.description ?? undefined,
      emoji: link.emoji ?? undefined,
      thumbnailUrl: link.thumbnailUrl ?? undefined,
      clicks: link.clicks ?? undefined,
      date: link.date ?? undefined,
    }))
  )

  const needsGradientBg = $derived(['glass', 'ocean'].includes(theme))
  const needsAuroraBg = $derived(theme === 'aurora')
  const needsBackdrop = $derived(['glass', 'aurora', 'ocean'].includes(theme))
  const isBento = $derived(theme === 'bento')
  const isMinimal = $derived(theme === 'minimal')
  const isNeubrutalism = $derived(theme === 'neubrutalism')
  const isNature = $derived(theme === 'nature')
  const socialIconStyle = $derived(themeConfig.social_icon_style || 'filled')
  const badgeColor = $derived(themeConfig.badge_color || 'blue')
  const hideBranding = $derived(Boolean(themeConfig.hide_branding))
  const wallpaperStyle = $derived(themeConfig.wallpaper_style || 'fill')
  const hasPattern = $derived(wallpaperStyle === 'pattern' && Boolean(themeConfig.wallpaper_pattern))
  const hasBlur = $derived(wallpaperStyle === 'blur')
  // Image backgrounds can land tuned-but-still-bright regions (latte foam,
  // beach sand, fashion whites) directly under the display name and bio.
  // We add a soft text-shadow on the header text when an image wallpaper is
  // active so dark characters keep their edge against cream highlights.
  const hasImageBg = $derived(wallpaperStyle === 'image' && Boolean(themeConfig.bg_image_url))
  const headerLayout = $derived(themeConfig.header_layout || 'classic')
  const isHeroHeader = $derived(headerLayout === 'hero')

  const maxVisiblePts = 3
  const hasMorePts = $derived(total_presentations_count > maxVisiblePts)
  const remainingPtsCount = $derived(total_presentations_count - maxVisiblePts)
  const visiblePts = $derived(hasMorePts ? presentations.slice(0, maxVisiblePts) : presentations)
  const overlayPt = $derived(hasMorePts && presentations.length > maxVisiblePts ? presentations[maxVisiblePts] : null)

  async function handleShare() {
    const url = typeof window !== 'undefined' ? window.location.href : ''
    const title = `${profile.displayName} (@${profile.username})`
    if (navigator.share) {
      try { await navigator.share({ title, url }) } catch {}
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url)
    }
  }
</script>

<div
  class="public-profile-scope profile-page"
  class:theme-glass={needsGradientBg}
  class:theme-aurora={needsAuroraBg}
  class:theme-minimal={isMinimal}
  class:theme-neubrutalism={isNeubrutalism}
  class:theme-nature={isNature}
  class:needs-backdrop={needsBackdrop}
  class:has-pattern={hasPattern}
  class:has-blur={hasBlur}
  class:has-image-bg={hasImageBg}
  class:hero-header={isHeroHeader}
  class:compact
  style={styleVars}
>
  {#if hasPattern}
    <div class="wallpaper-layer wallpaper-pattern" aria-hidden="true"></div>
  {/if}
  {#if hasBlur}
    <div class="wallpaper-layer wallpaper-blur" aria-hidden="true"></div>
  {/if}

  {#if needsAuroraBg}
    <div class="aurora-blobs">
      <div class="aurora-blob blob-1"></div>
      <div class="aurora-blob blob-2"></div>
      <div class="aurora-blob blob-3"></div>
    </div>
  {/if}

  {#if showTopActions}
    <div class="top-actions">
      <button type="button" class="top-action-btn" title="공유" onclick={handleShare}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
          <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
        </svg>
      </button>
      {#if is_owner}
        <a href="/my-profile/editor/design" class="top-action-btn" title="프로필 편집">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </a>
      {/if}
    </div>
  {/if}

  <div class="profile-container" class:bento-container={isBento}>
    <div class="profile-header">
      <ProfileAvatar initial={profile.avatarInitial} {theme} />
      <ProfileHeader
        displayName={profile.displayName}
        username={profile.username}
        department={profile.department}
        bio={profile.bio}
        isVerified={profile.isVerified}
        role={String(profile.role)}
        {badgeColor}
        {theme}
      />
      <ProfileSocialRow links={socialLinks} iconStyle={socialIconStyle as 'filled' | 'outline' | 'minimal'} {theme} />
      {#if showStats}
        <ProfileStatsRow
          stats={[
            { value: profile.stats.posts, label: '게시글', href: `/@${profile.username}/posts` },
            { value: profile.stats.saves, label: '팔로워', href: `/@${profile.username}/followers` },
            ...(profile.stats.presentations > 0 ? [{ value: profile.stats.presentations, label: '발표자료', href: is_owner ? '/my-profile/presentations' : `/pt` }] : []),
          ]}
          {theme}
        />
      {/if}
    </div>

    <ProfileLinkList links={normalizedLinks} layout={linkLayout} {theme} />

    {#if presentations.length > 0}
      <div class="presentations-section">
        <div class="pt-section-header">
          <h3 class="presentations-heading">Presentations</h3>
          {#if is_owner}
            <a href="/my-profile/presentations" class="pt-manage-link">관리 →</a>
          {/if}
        </div>
        <div class="presentations-grid">
          {#each visiblePts as pt (pt.slug)}
            <a href="/pt/{pt.slug}" class="pt-card" target="_blank" rel="noopener">
              <div class="pt-thumb">
                {#if pt.thumbnail_url}
                  <img src={pt.thumbnail_url} alt={pt.title} />
                {:else}
                  <svg class="pt-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                    <path stroke-linecap="square" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
                  </svg>
                {/if}
                {#if pt.category}
                  <span class="pt-badge">{pt.category}</span>
                {/if}
              </div>
              <div class="pt-info">
                <span class="pt-title">{pt.title}</span>
                {#if pt.category}
                  <span class="pt-category-label">{pt.category}</span>
                {/if}
              </div>
            </a>
          {/each}
          {#if hasMorePts}
            <a href="/@{profile.username}/presentations" class="pt-card pt-more-card">
              <div class="pt-thumb">
                {#if overlayPt?.thumbnail_url}
                  <img src={overlayPt.thumbnail_url} alt="더보기" />
                {:else}
                  <svg class="pt-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                    <path stroke-linecap="square" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
                  </svg>
                {/if}
                <div class="pt-more-overlay">
                  <span class="pt-more-count">+{remainingPtsCount}</span>
                </div>
              </div>
            </a>
          {/if}
        </div>
      </div>
    {/if}

    {#if shortUrl}
      <ProfileShareCard {shortUrl} copied={copiedShortUrl} onCopy={onCopyShortUrl} />
    {/if}

    {#if emailCaptureEnabled}
      <ProfileCaptureCard
        title={emailCaptureTitle}
        email={subscriberEmail}
        name={subscriberName}
        message={subscribeMessage}
        error={subscribeError}
        {subscribing}
        onEmailChange={onSubscriberEmailChange}
        onNameChange={onSubscriberNameChange}
        {onSubscribe}
      />
    {/if}

    <ProfileFooter hidden={hideBranding} />
  </div>
</div>

<style>
  .profile-page {
    min-height: 100vh;
    display: flex;
    justify-content: center;
    background: var(--page-bg);
    font-family: var(--font-body, 'SUIT Variable', -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', system-ui, sans-serif);
    position: relative;
    overflow-x: hidden;
    padding: 40px 16px 60px;
  }

  .profile-page.compact {
    flex: 1;
    min-height: 100%;
    width: 100%;
    padding: 24px 14px 32px;
    overflow-y: auto;
  }

  .profile-page.compact .profile-container {
    max-width: 100%;
  }

  .profile-page.compact .profile-header {
    margin-bottom: 22px;
  }

  .profile-page.compact .presentations-section {
    margin-top: 22px;
  }

  .profile-page.compact :global(.avatar) {
    width: 80px;
    height: 80px;
    font-size: 28px;
    margin-bottom: 12px;
  }

  .profile-page.compact :global(.display-name) {
    line-height: 1.2;
    margin-bottom: 3px;
  }

  .profile-page.compact :global(.username-row) {
    gap: 4px;
    margin-bottom: 6px;
  }

  .profile-page.compact :global(.org-row) {
    margin-bottom: 6px;
  }

  .profile-page.compact :global(.department-badge) {
    padding: 3px 9px;
    font-size: 11px;
  }

  .profile-page.compact :global(.bio) {
    max-width: 272px;
    font-size: 13px;
    line-height: 1.55;
  }

  .profile-page.compact :global(.social-row-header) {
    gap: 8px;
    margin: 10px 0 12px;
  }

  .profile-page.compact :global(.social-icon) {
    width: 36px;
    height: 36px;
  }

  .profile-page.compact :global(.social-icon svg) {
    width: 16px;
    height: 16px;
  }

  .profile-page.compact :global(.stats-row) {
    gap: 16px;
    padding: 10px 18px;
  }

  .profile-page.compact :global(.stat-value) {
    font-size: 16px;
  }

  .profile-page.compact :global(.stat-label) {
    font-size: 11px;
  }

  .profile-page.compact :global(.links-list) {
    gap: 8px;
  }

  .profile-page.compact :global(.link-card) {
    padding: 12px 14px;
  }

  .profile-page.compact :global(.link-left) {
    gap: 10px;
  }

  .profile-page.compact :global(.link-title) {
    font-size: 13px;
  }

  .profile-page.compact :global(.link-desc) {
    font-size: 11px;
  }

  .profile-page.compact :global(.link-headline) {
    margin-top: 10px;
    gap: 8px;
  }

  /* Wallpaper overlay layers — rendered behind .profile-container.
     Pattern: repeating SVG tile using --wallpaper-pattern-image (set by resolveTheme).
     Blur: frosted-glass backdrop via backdrop-filter. */
  .wallpaper-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }

  .wallpaper-pattern {
    background-image: var(--wallpaper-pattern-image, none);
    background-size: var(--wallpaper-pattern-size, 120px 120px);
    background-repeat: repeat;
  }

  .wallpaper-blur {
    backdrop-filter: blur(var(--wallpaper-blur, 22px));
    -webkit-backdrop-filter: blur(var(--wallpaper-blur, 22px));
    background: color-mix(in srgb, var(--page-bg, #ffffff) 40%, transparent);
  }

  /* Hero header layout — larger avatar + bigger typography for a more prominent top section. */
  .profile-page.hero-header .profile-header {
    padding: 16px 0 28px;
    margin-bottom: 24px;
    position: relative;
  }

  .profile-page.hero-header :global(.display-name) {
    font-size: calc(var(--title-size, 22px) * 1.25);
  }

  /* Header text polish for image backgrounds — dark title/bio text can
     disappear against cream-colored highlights in latte / beach / fashion
     photos. A soft shadow keeps the glyph edges visible without demanding
     white text (which would fight the coffee / warm color story). Scoped
     to .has-image-bg so fill/gradient/pattern wallpapers are untouched. */
  .profile-page.has-image-bg :global(.display-name),
  .profile-page.has-image-bg :global(.bio),
  .profile-page.has-image-bg :global(.department),
  .profile-page.has-image-bg :global(.username) {
    /* Double-layer halo: the tight black shadow draws an edge against
       bright highlights, the wider light-color glow gives separation
       against deep shadows in the same photo. Works for both light-on-
       dark and dark-on-light image combos. */
    text-shadow:
      0 1px 2px rgba(15, 23, 42, 0.65),
      0 0 18px rgba(15, 23, 42, 0.35);
  }

  .top-actions {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 50;
    display: flex;
    gap: 8px;
  }

  .top-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    color: var(--text-primary, #1a1a1a);
    background: var(--card-bg, rgba(255,255,255,0.9));
    border: 1px solid var(--card-border, rgba(0,0,0,0.1));
    border-radius: 9999px;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    text-decoration: none;
    cursor: pointer;
    transition: all 150ms ease;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    font-family: inherit;
  }

  .top-action-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  }

  .theme-glass {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .theme-aurora {
    background: #0f0c29;
  }

  .aurora-blobs {
    position: fixed;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
  }

  .aurora-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.5;
    animation: aurora-float 12s ease-in-out infinite alternate;
  }

  .blob-1 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, #FF6B6B 0%, transparent 70%);
    top: -100px;
    right: -50px;
    animation-delay: 0s;
  }

  .blob-2 {
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, #4ECDC4 0%, transparent 70%);
    bottom: 20%;
    left: -80px;
    animation-delay: -4s;
  }

  .blob-3 {
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, #45B7D1 0%, transparent 70%);
    top: 40%;
    right: -40px;
    animation-delay: -8s;
  }

  @keyframes aurora-float {
    0% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -20px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.95); }
    100% { transform: translate(10px, -10px) scale(1.05); }
  }

  .profile-container {
    width: 100%;
    max-width: 448px;
    position: relative;
    z-index: 1;
  }

  .bento-container {
    max-width: 512px;
  }

  .profile-header {
    text-align: center;
    margin-bottom: 32px;
  }

  .presentations-section {
    margin-top: 28px;
  }

  .pt-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .presentations-heading,
  .pt-manage-link,
  .pt-title,
  .pt-category-label {
    color: var(--text-primary);
  }

  .pt-manage-link {
    text-decoration: none;
    font-size: 13px;
    opacity: 0.75;
  }

  .presentations-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .pt-card {
    text-decoration: none;
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 18px;
    overflow: hidden;
    box-shadow: var(--card-shadow);
  }

  .pt-thumb {
    aspect-ratio: 16 / 9;
    overflow: hidden;
    position: relative;
    background: color-mix(in srgb, var(--accent) 10%, transparent);
  }

  .pt-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .pt-icon {
    width: 36px;
    height: 36px;
    position: absolute;
    inset: 0;
    margin: auto;
    color: var(--text-secondary);
  }

  .pt-badge,
  .pt-more-overlay {
    position: absolute;
  }

  .pt-badge {
    left: 10px;
    top: 10px;
    padding: 4px 8px;
    font-size: 11px;
    border-radius: 9999px;
    background: rgba(255,255,255,0.85);
    color: #111827;
  }

  .pt-info {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .pt-title {
    font-weight: 600;
    font-size: 14px;
  }

  .pt-category-label {
    font-size: 12px;
    opacity: 0.7;
  }

  .pt-more-overlay {
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15,23,42,0.48);
  }

  .pt-more-count {
    color: white;
    font-size: 24px;
    font-weight: 700;
  }
</style>
